import { useQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';
import { GET_CARD_INVOICES_PAID_REMAINS_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { CardInvoicesCustomerDataResponse } from '../../integrations/allkort/graphql/interface/Customer';

const BATCH_SIZE = 5;
const MAX_INVOICES = 500;

/**
 * Custom hook for fetching ALL card invoice paid/remains data with automatic pagination
 * Fetches invoice payment details (paid, remains) for all invoices
 * 
 * Usage:
 * const { paidRemains, loading, error, isComplete } = useGetCardInvoicesPaidRemains();
 * 
 * @returns Object with paidRemains array, loading state, error, and completion flag
 */
export const useGetCardInvoicesPaidRemains = () => {
	const [allPaidRemains, setAllPaidRemains] = useState<any[]>([]);
	const [currentCursor, setCurrentCursor] = useState<string | null>(null);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const [isComplete, setIsComplete] = useState(false);

	const variables = useMemo(() => ({
		first: BATCH_SIZE,
		after: currentCursor,
		before: null,
		last: null
	}), [currentCursor]);

	const { data, loading, error } = useQuery<CardInvoicesCustomerDataResponse>(
		GET_CARD_INVOICES_PAID_REMAINS_QUERY,
		{
			variables,
			fetchPolicy: 'cache-first',
			skip: isComplete,
		}
	);

	useEffect(() => {
		if (!data?.currentCustomer?.cardInvoices) return;

		const { nodes, pageInfo } = data.currentCustomer.cardInvoices;

		if (nodes && nodes.length > 0) {
			setAllPaidRemains(prev => {
				// If this is the first fetch (no cursor), replace all data
				if (!currentCursor) {
					return nodes;
				}
				// Otherwise append new items, avoiding duplicates
				const existingIds = new Set(prev.map(item => item.id));
				const newItems = nodes.filter(item => !existingIds.has(item.id));
				return [...prev, ...newItems];
			});

			// Check if we should fetch more
			const totalItems = currentCursor ? allPaidRemains.length + nodes.length : nodes.length;
			
			if (pageInfo.hasNextPage && totalItems < MAX_INVOICES) {
				setCurrentCursor(pageInfo.endCursor);
				setIsFetchingMore(true);
			} else {
				setIsFetchingMore(false);
				setIsComplete(true);
			}
		} else {
			setIsFetchingMore(false);
			setIsComplete(true);
		}
	}, [data, currentCursor]);

	return {
		paidRemains: allPaidRemains,
		loading: loading || isFetchingMore,
		error,
		isComplete,
	};
};
